import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useClientByIdQuery,
  useClientDetailsUpdateMutation,
  useClientFileCreateMutation,
  useClientFileDeleteMutation,
  useClientTagsQuery,
  useClientTypeQuery,
  useCountrySelectListQuery,
  useLanguageQuery,
} from 'api';
import { FormInstance, FormProps, notification, Upload } from 'components';
import { MAX_FILE_SIZE } from 'constants/index';
import { CLIENT_PRIORITY_LEVEL } from 'staticData/form';
import { ErrorMappingValueType } from 'typings';
import { ClientDetailsFormType, ClientDetailsSelectType } from 'typings/form/client/details';
import { IClientDetailsUpdate } from './index';
import { useWatch } from 'antd/es/form/Form';

export const useClientsDetailsEdit = (props: IClientDetailsUpdate & { form: FormInstance }) => {
  const { onCancel, form, isOpen } = props;
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const { id } = useParams() as { id: string };
  const priorityLevel = useWatch('priorityLevel', form);
  const { data: clientDetails = {} } = useClientByIdQuery({ id });
  const { data: clientTypeData = [] } = useClientTypeQuery();
  const { data: languageData = [] } = useLanguageQuery();
  const { data: tagData = [] } = useClientTagsQuery();
  const { data: countryData = [] } = useCountrySelectListQuery();

  const [updateClientDetails, { isLoading: detailsUpdateIsLoading }] = useClientDetailsUpdateMutation();
  const [clientFileCreate, { isLoading: fileCreateIsLoading }] = useClientFileCreateMutation();
  const [clientFileDelete, { isLoading: fileDeleteIsLoading }] = useClientFileDeleteMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        name: clientDetails.name,
        verified: clientDetails.verified,
        webSiteUrl: clientDetails.webSiteUrl,
        countryId: clientDetails.countryId,
        clientTypeId: clientDetails.clientTypeId,
        manualTags: clientDetails.manualTags,
        languageId: clientDetails.languageId,
        priorityLevel: clientDetails.priorityLevel,
        priorityRationale: clientDetails.priorityRationale,
        additionalNotes: clientDetails.additionalNotes,
        logo: clientDetails.files?.LOGO
          ? [
              {
                uid: '-1',
                status: 'done',
                name: clientDetails.files.LOGO.split('fileName=')?.[1] || 'Client Logo',
                imgUrl: clientDetails.files?.LOGO,
              },
            ]
          : [],
      });
    }
  }, [clientDetails, isOpen, form]);

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setFieldValue('logo', []);
      form.setFields([
        {
          name: 'logo',
          errors: [`File exceeds the maximum size of 5 MB.`],
        },
      ]);
      setIsSaveBtnDisabled(true);
      return Upload.LIST_IGNORE;
    } else {
      form.setFields([
        {
          name: 'logo',
          errors: undefined,
        },
      ]);
      setIsSaveBtnDisabled(false);
    }
    return false;
  };

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(true);
    onCancel();
  }, [form, onCancel]);

  const onFinish: FormProps<ClientDetailsFormType>['onFinish'] = useCallback(
    (values: ClientDetailsFormType) => {
      const { logo } = form.getFieldsValue();
      const payload = { ...values, logo: undefined };
      const shouldDeleteLogo = clientDetails.files?.LOGO && !logo?.length;
      payload.id = id;
      const formData = new FormData();
      if (logo?.length && id && logo[0]?.uid !== '-1') {
        const file = logo[0];
        formData.append('files', file.originFileObj, file.name);
        formData.append('clientId', id);
      }
      updateClientDetails(payload)
        .then(({ data, error }: any) => {
          if (data?.success) {
            form.resetFields();
            if (shouldDeleteLogo) {
              const [_, fileName] = clientDetails.files.LOGO.split('fileName=');
              clientFileDelete({ name: fileName, id })
                .then(({ data, error }: any) => {
                  if (data?.success) {
                    openNotificationWithIcon({
                      type: 'success',
                      message: 'The client details was successfully updated.',
                    });
                    onCancel();
                    setIsSaveBtnDisabled(true);
                  } else if (!error.data.success) {
                    const { data = {} } = error.data;
                    Object.keys(data).forEach((_key: string) => {
                      openNotificationWithIcon({
                        type: 'error',
                        message: data[_key],
                      });
                    });
                  } else if (error.data.error) {
                    openNotificationWithIcon({
                      type: 'error',
                      message: 'Something went wrong!',
                    });
                  }
                })
                .catch((err) => {
                  console.log(err, 'error');
                });
            } else if (formData.has('files')) {
              clientFileCreate(formData)
                .then(({ data, error }: any) => {
                  if (data?.success) {
                    openNotificationWithIcon({
                      type: 'success',
                      message: 'The client details was successfully updated.',
                    });
                    onCancelModal();
                  } else if (!error.data.success) {
                    const { data = {} } = error.data;
                    Object.keys(data).forEach((_key: string) => {
                      openNotificationWithIcon({
                        type: 'error',
                        message: data[_key],
                      });
                    });
                  } else if (error.data.error) {
                    openNotificationWithIcon({
                      type: 'error',
                      message: 'Something went wrong!',
                    });
                  }
                })
                .catch((err) => {
                  console.log(err, 'error');
                });
            } else {
              openNotificationWithIcon({
                type: 'success',
                message: 'The client details was successfully updated.',
              });
              onCancelModal();
            }
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [
      form,
      updateClientDetails,
      openNotificationWithIcon,
      onCancelModal,
      id,
      clientFileCreate,
      clientFileDelete,
      clientDetails,
      onCancel,
    ]
  );
  const SELECT_TYPE_MAPPING: Record<ClientDetailsSelectType, any> = {
    main_language: languageData,
    client_type: clientTypeData,
    main_country: countryData,
    priority_level: CLIENT_PRIORITY_LEVEL,
  };

  useEffect(() => {
    if (priorityLevel === 'STANDARD') {
      const hasError = form.getFieldError('priorityRationale')?.length;
      if (hasError) {
        form.setFields([
          {
            name: 'priorityRationale',
            errors: [], // Clears the error for filed
          },
        ]);
      }
    }
  }, [priorityLevel, form]);

  return {
    SELECT_TYPE_MAPPING,
    onFinish,
    contextHolder,
    tagData,
    isSaveBtnDisabled,
    handleFileSizeValidation,
    isLoading: detailsUpdateIsLoading || fileCreateIsLoading || fileDeleteIsLoading,
    priorityLevel,
  };
};
